import {
  channelsList,
  channelById,
  createChannel,
  updateChannel,
  deleteChannel,
  testChannel,
  dealTestChannel,
  revealChannelSecret,
  triggersList,
  createTrigger,
  updateTrigger,
  deleteTrigger,
  fetchEventKeys,
  targetsList,
  createTarget,
  updateTarget,
  deleteTarget,
} from '@/services/webhookChannels.service'

export default {
  namespaced: true,
  state: {
    channels: { data: [], meta: { pagination: { total: 0 } } },
    triggers: [],
    targets: [],
    eventKeys: [],
    status: 'init',
    triggerStatus: 'init',
    targetStatus: 'init',
    testStatus: 'idle',
  },
  getters: {
    getChannels: s => s.channels,
    getTriggers: s => s.triggers,
    getTargets: s => s.targets,
    getEventKeys: s => s.eventKeys,
    getStatus: s => s.status,
    getTriggerStatus: s => s.triggerStatus,
    getTargetStatus: s => s.targetStatus,
    getTestStatus: s => s.testStatus,
  },
  mutations: {
    SET_CHANNELS(state, value) { state.channels = value },
    SET_TRIGGERS(state, value) { state.triggers = value },
    SET_TARGETS(state, value) { state.targets = value },
    SET_EVENT_KEYS(state, value) { state.eventKeys = value },
    SET_STATUS(state, value) { state.status = value },
    SET_TRIGGER_STATUS(state, value) { state.triggerStatus = value },
    SET_TARGET_STATUS(state, value) { state.targetStatus = value },
    SET_TEST_STATUS(state, value) { state.testStatus = value },
  },
  actions: {
    async fetchChannels({ commit }, params) {
      commit('SET_STATUS', 'pending')
      try {
        const { data } = await channelsList(params)
        commit('SET_CHANNELS', data)
      } catch (e) {
        this._vm.$root.$notify({ message: e?.response?.data?.message || 'Error', type: 'danger' })
      } finally {
        commit('SET_STATUS', 'init')
      }
    },

    // `extra` carries list params the caller needs again after the refresh (the Deals screen asks for
    // include=triggers), so the refresh here does not drop them.
    async saveChannel({ dispatch }, { form, page, perPage, extra = {} }) {
      const { data } = form.id ? await updateChannel(form.id, form) : await createChannel(form)
      dispatch('fetchChannels', { page, per_page: perPage, ...extra })

      return data?.data || data
    },

    async removeChannel({ dispatch }, { id, page, perPage, extra = {} }) {
      await deleteChannel(id)
      dispatch('fetchChannels', { page, per_page: perPage, ...extra })
    },

    // Returns the value to the caller rather than committing it to the store:
    // it should live as long as the dialog that asked for it, not in shared
    // state every other screen can read.
    async revealSecret(_, id) {
      const { data } = await revealChannelSecret(id)

      return data?.data || data
    },

    async testWebhookChannel({ commit }, id) {
      commit('SET_TEST_STATUS', 'pending')
      try {
        await testChannel(id)
        commit('SET_TEST_STATUS', 'success')
      } catch (e) {
        commit('SET_TEST_STATUS', 'error')
        throw e
      } finally {
        setTimeout(() => commit('SET_TEST_STATUS', 'idle'), 3000)
      }
    },

    // Returns the API's answer to the caller (the dialog that asked); a refusal is thrown with
    // e.response.data = { code, message } or { data: { status, reason } }.
    async testDealChannel(_, id) {
      const { data } = await dealTestChannel(id)

      return data?.data || data
    },

    async fetchTriggers({ commit }, channelId) {
      commit('SET_TRIGGER_STATUS', 'pending')
      try {
        const { data } = await triggersList(channelId)
        commit('SET_TRIGGERS', data.data || data)
      } catch (e) {
        this._vm.$root.$notify({ message: e?.response?.data?.message || 'Error', type: 'danger' })
      } finally {
        commit('SET_TRIGGER_STATUS', 'init')
      }
    },

    async saveTrigger({ dispatch }, { channelId, form }) {
      if (form.id) {
        await updateTrigger(channelId, form.id, form)
      } else {
        await createTrigger(channelId, form)
      }
      dispatch('fetchTriggers', channelId)
    },

    async removeTrigger({ dispatch }, { channelId, triggerId }) {
      await deleteTrigger(channelId, triggerId)
      dispatch('fetchTriggers', channelId)
    },

    async fetchEventKeys({ commit }) {
      try {
        const { data } = await fetchEventKeys()
        commit('SET_EVENT_KEYS', data.data || data)
      } catch {
        // fallback: leave empty, component provides defaults
      }
    },

    async fetchTargets({ commit }, channelId) {
      commit('SET_TARGET_STATUS', 'pending')
      try {
        const { data } = await targetsList(channelId)
        commit('SET_TARGETS', data.data || data)
      } catch (e) {
        this._vm.$root.$notify({ message: e?.response?.data?.message || 'Error', type: 'danger' })
      } finally {
        commit('SET_TARGET_STATUS', 'init')
      }
    },

    async saveTarget({ dispatch }, { channelId, form }) {
      if (form.id) {
        await updateTarget(channelId, form.id, form)
      } else {
        await createTarget(channelId, form)
      }
      dispatch('fetchTargets', channelId)
    },

    async removeTarget({ dispatch }, { channelId, targetId }) {
      await deleteTarget(channelId, targetId)
      dispatch('fetchTargets', channelId)
    },
  },
}
